import _ from 'lodash';

import urls from '../urls';
import config from '../config';
import {
    ActionTypes,
    TimeDirections,
    JourneyPlannerLayout,
    DelayRepayPaymentMethod,
    GenericModals,
    CookieKeys
} from '../constants/AppConstants';
import BaseStore from './BaseStore';
import { translate as t } from '../lib/I18n';
import { hotJarTrigger } from '../helpers/HotJar';
import EcommerceHelper from '../helpers/DataLayerEcommerce';
import CookieHelper from '../helpers/Cookie';
import UserStore from '../stores/UserStore';
import { toPounds } from '../helpers/Currency';
import { isGtmConsentGranted, getGtmConsentState } from '../helpers/UsercentricsConsent';

let cachedUserIDHashed = null;
let cachedUserIDHashPending = null;
let cachedPaymentMethod = null;

// Events fired while Usercentrics hasn't answered yet (consent 'pending') are queued here
// instead of being dropped, then replayed once UC settles on a granted/denied consent state.
const pendingConsentEvents = [];
let consentListenersAttached = false;

function pushToDataLayer (dataLayerName, payload) {
    if (window.hasOwnProperty(dataLayerName)) {
        window[dataLayerName].push(payload);
    }
}

function detachConsentListeners () {
    if (!consentListenersAttached || typeof window === 'undefined' || !window.removeEventListener) {
        return;
    }
    consentListenersAttached = false;
    window.removeEventListener('UC_UI_INITIALIZED', flushPendingConsentEvents);
    window.removeEventListener('ucConsentEvent', flushPendingConsentEvents);
}

function flushPendingConsentEvents () {
    if (!pendingConsentEvents.length) { return; }

    const state = getGtmConsentState();
    if (state === 'pending') { return; }

    const queued = pendingConsentEvents.splice(0, pendingConsentEvents.length);
    if (state === 'granted') {
        queued.forEach(({ dataLayerName, payload }) => pushToDataLayer(dataLayerName, payload));
    }
    // Consent has settled (granted or denied) — nothing left to flush for, so stop listening.
    detachConsentListeners();
}

function queuePendingEvent (dataLayerName, payload) {
    pendingConsentEvents.push({ dataLayerName, payload });

    if (consentListenersAttached || typeof window === 'undefined' || !window.addEventListener) {
        return;
    }
    consentListenersAttached = true;
    // 'UC_UI_INITIALIZED' fires once the CMP finishes booting; 'ucConsentEvent' fires when the
    // user actually makes a choice — either can be the moment the pending state resolves.
    window.addEventListener('UC_UI_INITIALIZED', flushPendingConsentEvents);
    window.addEventListener('ucConsentEvent', flushPendingConsentEvents);
}

function computeUserIDHash(userID) {
    if (!userID || typeof window === 'undefined' || !window.crypto || !window.crypto.subtle ||
        typeof TextEncoder === 'undefined') {
        return null;
    }
    const bytes = new TextEncoder().encode(String(userID));
    return window.crypto.subtle.digest('SHA-256', bytes).then(function(buf) {
        return Array.from(new Uint8Array(buf))
            .map(function(b) { return ('00' + b.toString(16)).slice(-2); })
            .join('');
    }).catch(function() {
        return null;
    });
}

function scheduleUserIDHash(userID) {
    if (!userID || cachedUserIDHashed !== null || cachedUserIDHashPending) { return; }
    cachedUserIDHashPending = computeUserIDHash(userID);
    if (!cachedUserIDHashPending) { return; }
    cachedUserIDHashPending.then(function(hash) {
        cachedUserIDHashed = hash;
        cachedUserIDHashPending = null;
    });
}


export function location () {
    const { pathname, search } = window.location;
    return pathname + search;
}

export const cleanEventName = (event) => {
    return event.toLowerCase().replace(/ |_/g, '-');
};

class DataLayerStore extends BaseStore {

    getActionHandlers () {
        return {
            [ ActionTypes.TRACKING_ENABLED]: this.trackingEnabled,
            [ ActionTypes.TRACKING_DISABLED]: this.trackingDisabled,
            [ ActionTypes.ROUTE_CHANGED ]: this.pageView,
            [ ActionTypes.MODAL_OPENED ]: this.modalOpened,
            [ ActionTypes.GENERIC_MODAL_OPEN ]: this.genericModalOpened,

            [ ActionTypes.LOGGED_IN ]: this.loggedIn,
            [ ActionTypes.LOGGED_OUT ]: this.simpleEvent,
            [ ActionTypes.REGISTRATION_SUCCESS ]: this.simpleEvent,
            [ ActionTypes.SMARTCARD_REGISTERED ]: this.simpleEvent,
            [ ActionTypes.SMARTCARD_LINKED ]: this.simpleEvent,
            [ ActionTypes.KEYGO_REGISTRATION_COMPLETE ]: this.simpleEvent,

            [ ActionTypes.STATION_FINDER_SET_EXPANDED ]: this.simpleEvent,

            [ ActionTypes.SEARCH_FINISHED ]: this.pointSearch,
            [ ActionTypes.SEASON_SEARCH_FINISHED ]: this.seasonSearch,

            [ ActionTypes.SHOW_EARLIER_TRAINS_STARTED ]: this.earlierLater(TimeDirections.EARLIER),
            [ ActionTypes.SHOW_LATER_TRAINS_STARTED ]: this.earlierLater(TimeDirections.LATER),

            [ ActionTypes.DELIVERY_METHOD ]: this.deliveryMethod,
            [ ActionTypes.PAYMENT_METHOD ]: this.paymentMethod,

            [ ActionTypes.ORDER_PAID ]: this.orderPaid,
            [ ActionTypes.ORDER_CREATED ]: this.orderCreated,
            [ ActionTypes.ORDER_TRIP_CREATED ]: this.addToBasket,
            [ ActionTypes.ORDER_TRIP_DELETED ]: this.removeFromBasket,
            [ ActionTypes.BASKET_CLEARED ]: this.event.bind(this, 'order:cleared', null),

            [ ActionTypes.WIDGET_SEARCH_STATION_SELECT ]: this.widgetStationSelector,
            [ ActionTypes.WIDGET_SEARCH_STATION_SWITCH ]: this.widgetStationSwitch,
            [ ActionTypes.WIDGET_SEARCH_CLICK ]: this.widgetSearchClick,
            [ ActionTypes.WIDGET_SEARCH_DAY_SELECT ]: this.widgetDaySelect,

            [ ActionTypes.FARES_EXPAND ]: this.faresExpand,
            [ ActionTypes.PLUS_BUS_SELECT ]: this.plusBusSelect,

            [ ActionTypes.EXTERNAL_LINK ]: this.externalLink,

            [ ActionTypes.JP_CHANGE_LAYOUT ]: this.jpChangeLayout,
            [ ActionTypes.JP_FARE_CLASS_FILTER_CLICK ]: this.jpChangeFareClassFilter,
            [ ActionTypes.JP_FARE_SELECTION_FILTER_CLICK ]: this.jpChangeFareSelectionFilter,
            [ ActionTypes.JP_TOC_SPECIFIC_FILTER_RENDERED ]: this.jpTocSpecificFilterRender,
            [ ActionTypes.JP_TOC_SPECIFIC_FILTER_CLICK ]: this.jpTocSpecificFilterClick,
            [ ActionTypes.JP_ALL_FARES_ACCORDION_CLICK ]: this.jpAllFaresAccordionClick,
            [ ActionTypes.JP_CHECKOUT_WITH_SUGGESTED_PRODUCTS ]: this.jpCheckoutWithSuggestedProducts,
            [ ActionTypes.JP_CHANGE_SEARCH_TYPE ]: this.jpChangedSearchType,

            [ ActionTypes.TUTORIAL_TOGGLE ]: this.handleTutorialToggle,

            [ ActionTypes.STATION_FINDER_SEARCH_STARTED ]: this.stationFinderSearch,
            [ ActionTypes.STATION_FINDER_STATION_SELECTED ]: this.stationFinderSelected,

            [ ActionTypes.FAVOURITES_ADDED ]: this.addFavourite,
            [ ActionTypes.FAVOURITES_REMOVED ]: this.removeFavourite,
            [ ActionTypes.FAVOURITE_ADDED_TO_BASKET ]: this.addFavouriteToBasket,
            [ ActionTypes.REORDER_ADDED_TO_BASKET ]: this.addReorderToBasket,

            [ ActionTypes.DISRUPTION_ALERT_CREATED ]: this.addDisruptionAlert,
            [ ActionTypes.DISRUPTION_ALERT_DELETED ]: this.deleteDisruptionAlert,

            [ ActionTypes.AUTO_DR_CLAIM_DISMISSED ]: this.event.bind(this, 'page:event:auto_dr_claim_dismissed', null),
            [ ActionTypes.AUTO_DR_CLAIM_SELECTED ]: this.event.bind(this, 'page:event:auto_dr_claim_selected', null),
            [ ActionTypes.AUTO_DR_CLAIM_SUBMITTED ]: this.submitAutoDRClaim,

            [ ActionTypes.RETURN_TRAVELCARD_CTA_CLICK ]: this.returnTravelcardAdded,

            [ ActionTypes.REFUND_COMPLETE ]: this.refundComplete,

            [ ActionTypes.AMEND_CTA_CLICKED ]: this.event.bind(this, 'page:event:amend-cta-clicked', null),
            [ ActionTypes.AMEND_CONFIRMED ]: this.confirmAmendBooking,

            [ ActionTypes.WIDGETS_RENDER_JP ]: this.widgetsRender,
            [ ActionTypes.WIDGETS_VIEW_RESULTS ]: this.widgetsViewResults,

            [ ActionTypes.RAILCARD_JOBCENTREPLUS_CHECKED ]: this.toggleJobcenterPlusRailcard,

            [ ActionTypes.SHOW_FLEXI_WARNING_MODAL ]: this.showFlexiWarningModal,
            [ ActionTypes.FLEXI_WARNING_MODAL_ACKNOWLEDGEMENT_CHECKBOX_CHECKED ]: this.flexiWarningModalAcknowledgementCheckboxChecked,
            [ ActionTypes.FLEXI_WARNING_MODAL_ACKNOWLEDGEMENT_CHECKBOX_UNCHECKED ]: this.flexiWarningModalAcknowledgementCheckboxUnchecked,
            [ ActionTypes.FLEXI_WARNING_MODAL_CLOSE ]: this.flexiWarningModalClose,
            [ ActionTypes.FLEXI_WARNING_MODAL_CONTINUE ]: this.flexiWarningModalContinue,
            [ ActionTypes.FLEXI_WARNING_MODAL_MORE_INFO_CLICKED ]: this.flexiWarningModalMoreInfoClicked,

            [ ActionTypes.USER_OPTED_IN ]: this.userOptedIn,
            [ ActionTypes.USER_OPTED_OUT ]: this.userOptedOut,
            [ ActionTypes.SELECT_NOTIFICATION_PREFERENCE ]: this.selectNotificationPreference,
            [ ActionTypes.SELECT_REPAYMENT_METHOD ]: this.selectRepaymentMethod,
            [ ActionTypes.AMEND_NOTIFICATION_PREFERENCE ]: this.amendNotificationPreference,
            [ ActionTypes.AMEND_REPAYMENT_METHOD ]: this.amendRepaymentMethod,
            [ ActionTypes.AMEND_ADDRESS ]: this.amendAddress,

            [ ActionTypes.SAVE_REGULAR_JOURNEY_WITH_OCDR_FAV_JOURNEY ]: this.saveRegularJourneyWithOCDRFavJourney,
            [ ActionTypes.WEBCHAT_OPENED ]: this.webchatOpened,

            [ ActionTypes.BACK_FROM_PAYMENT_PAGE ]: this.pageEvent,
            [ ActionTypes.ADD_ANOTHER_JOURNEY_ON_DELIVERY_PAGE ]: this.pageEvent,
            [ ActionTypes.ADD_ANOTHER_JOURNEY_ON_PAYMENT_PAGE ]: this.pageEvent,
            [ ActionTypes.ADD_ANOTHER_JOURNEY_ON_BASKET_MODAL ]: this.modalEvent,
            [ ActionTypes.DELIVERY_SCREEN_BACK_NAVIGATION_TICKET_REMOVED ]: this.modalEvent,
            [ ActionTypes.DELIVERY_SCREEN_BACK_NAVIGATION_TICKET_KEPT ]: this.modalEvent,

            [ ActionTypes.CREATE_ACCESSIBILITY_PROFILE_LOADED ]: this.createAccessibilityProfile,
            [ ActionTypes.UPDATE_ACCESSIBILITY_PROFILE_LOADED ]: this.updateAccessibilityProfile,
            [ ActionTypes.BOOK_ASSIST_SUCCESS ]: this.bookPassengerAssist,
            [ ActionTypes.BOOK_MANY_ASSISTS_SUCCESS ]: this.bookPassengerAssistWithOrder,
            [ ActionTypes.CANCEL_ASSISTED_BOOKING_SUCCESS ]: this.cancelPassengerAssist,
            [ ActionTypes.EDIT_ASSISTED_BOOKING_SUCCESS ]: this.editPassengerAssist,
            [ ActionTypes.FETCH_CHILD_SMARTCARD_REPLACEMENT_STARTED ]: this.trackChildSmartcardReplacementPageView,
            [ ActionTypes.REQUEST_CHILD_SMARTCARD_REPLACEMENT_STARTED ]: this.trackChildSmartcardReplacementRequest,

            [ ActionTypes.APPLY_VOUCHER_CODE_FINISHED ]: this.voucherApplied,
        };
    }

    simpleEvent ({ actionType }) {
        this.event(actionType.toLowerCase());

        if (actionType === ActionTypes.LOGGED_OUT) {
            cachedUserIDHashed = null;
            cachedUserIDHashPending = null;
            cachedPaymentMethod = null;
            this.removeTripWithPassengerAssist();
        }
    }

    pageEvent ({ context }) {
        this.event(`page:event:${context}`);
    }

    modalEvent ({ context }) {
        this.event(`modal:event:${context}`);
    }

    event (name, data) {
        if (this.name === 'order:cleared') {
            this.removeTripWithPassengerAssist();
        }

        if (CookieHelper.get(CookieKeys.DO_NOT_TRACK)) {
            return;
        }

        const consentState = getGtmConsentState();
        if (consentState === 'denied') {
            return;
        }

        const dataLayerName = config.get('gtmDataLayer');
        const user = UserStore.getCurrent();
        const userID = user && user.customer && user.customer.id;

        scheduleUserIDHash(userID);

        const payload = Object.assign({
            event: `OTRL:${name}`,
            userID,
            ...(cachedUserIDHashed ? { userIDHashed: cachedUserIDHashed } : {}),
        }, data);

        if (consentState === 'pending') {
            queuePendingEvent(dataLayerName, payload);
            return;
        }

        pushToDataLayer(dataLayerName, payload);
    }

    isSitecoreEnabled () {
        return this.isTrackingEnabled() &&
            Boolean(config.get('sitecoreClientKey')) &&
            typeof window !== 'undefined' &&
            Boolean(window.engage);
    }

    sitecoreView () {
        if (!this.isSitecoreEnabled()) { return; }
        window.engage.pageView({
            channel: 'WEB',
            language: 'EN',
            currency: 'GBP',
            page: window.location.pathname,
        });
    }

    sitecoreIdentity (hashedId) {
        if (!hashedId || !this.isSitecoreEnabled()) { return; }
        window.engage.event('IDENTITY', {
            channel: 'WEB',
            currency: 'GBP',
            language: 'EN',
            page: window.location.pathname,
            identifiers: [{
                id: hashedId,
                provider: 'OTRL_ID',
            }],
        });
    }

    sitecoreSearch (search, isSeason) {
        if (!this.isSitecoreEnabled()) { return; }
        const serialized = search.serialize();
        const params = serialized.params || {};
        const query = serialized.query || {};
        const productType = isSeason ? 'season' : (search.ticketType ? search.ticketType.toLowerCase() : '');

        function isReal(v) {
            return v !== null && v !== undefined && v !== '';
        }

        let extensions;
        if (isSeason) {
            extensions = {};
            if (isReal(params.duration)) { extensions.search_season_duration = params.duration; }
            if (isReal(params.startDate)) { extensions.search_season_start_Date = params.startDate; }
            if (isReal(params.passengerType)) { extensions.search_season_passenger_Type = params.passengerType; }
            if (isReal(params.railcardCode)) { extensions.search_season_railcard_Code = params.railcardCode; }
        } else {
            const fields = {
                search_adults: params.adults,
                search_children: params.children,
                search_depart_Now: query.departNow,
                search_origin: params.origin,
                search_origin_Name: search.origin.name,
                search_destination: params.destination,
                search_destination_Name: search.destination.name,
                search_outbound_Date_Time: params.outboundDateTime,
                search_real_Time: query.realTime,
                search_search_Preferences: query.searchPreferences,
                search_show_Additional_Routes: query.showAdditionalRoutes,
                search_show_Cheapest: query.showCheapest,
                search_smart_card: search.smartcard,
                search_num_Journeys: params.numJourneys,
                search_toc_Specific: query.tocSpecific,
                search_target_Layout: params.targetLayout,
                search_railcards: params.railcards,
            };
            extensions = {};
            Object.keys(fields).forEach(function(key) {
                if (isReal(fields[key])) { extensions[key] = fields[key]; }
            });
            if (productType === 'return' && isReal(params.returnDateTime)) {
                extensions.search_return_Date_Time = params.returnDateTime;
            }
        }

        window.engage.event('SEARCH', {
            channel: 'WEB',
            currency: 'GBP',
            language: 'EN',
            page: window.location.pathname,
            product_name: search.origin.name + ' to ' + search.destination.name,
            product_type: productType,
            extensions,
        });
    }

    sitecoreOrderCheckout (order) {
        if (!this.isSitecoreEnabled()) { return; }
        const orderedAt = new Date().toISOString();
        const orderItems = [];
        order.trips.forEach((trip) => {
            EcommerceHelper.productsFromTrip(trip).forEach((product, i) => {
                orderItems.push({
                    type: product.category,
                    referenceId: product.id + '-' + i,
                    orderedAt,
                    status: 'PURCHASED',
                    currencyCode: 'GBP',
                    price: product.price,
                    name: product.name,
                    productId: product.id,
                    quantity: product.quantity,
                    variant: product.variant,
                    extensions: [{
                        name: 'ext',
                        key: 'default',
                        purchase_item_origin: product.dimension6,
                        purchase_item_destination: product.dimension7,
                        route_code: product.dimension8,
                        purchase_item_return: product.dimension9,
                        purchase_item_season: product.dimension10,
                        purchase_item_advance: product.dimension11,
                        purchase_item_package: product.dimension12,
                        purchase_item_first_class: product.dimension13,
                        validity_code: product.dimension14,
                        restriction_code: product.dimension15,
                        cross_london: product.dimension16,
                        promotion_code: product.dimension17,
                        purchase_item_outbound_dt: product.dimension18,
                        purchase_item_return_dt: product.dimension19,
                    }],
                });
            });
        });
        window.engage.event('ORDER_CHECKOUT', {
            channel: 'WEB',
            currency: 'GBP',
            language: 'EN',
            page: window.location.pathname,
            order: {
                referenceId: order.id,
                orderedAt,
                status: 'PURCHASED',
                currencyCode: 'GBP',
                price: toPounds(order.totalPrice),
                paymentType: cachedPaymentMethod || '',
                cardType: 'Unknown',
                extensions: [{
                    name: 'ext',
                    key: 'default',
                    affiliation: config.get('brand'),
                    orderCoupon: order.getDiscountCode() || '',
                }],
                orderItems,
            },
        });
    }

    pointSearch ({ search }) {
        const serialized = search.serialize();

        const data = Object.assign(
            serialized.params,
            serialized.query,
            {
                originName: search.origin.name,
                destinationName: search.destination.name,
                journeyType: search.ticketType.toLowerCase(),
                smartcard: search.smartcard
            }
        );
        this.event('search:point', data);
        this.sitecoreSearch(search, false);
    }

    seasonSearch ({ search }) {
        const serialized = search.serialize();

        const data = Object.assign(
            serialized.params,
            serialized.query,
            {
                originName: search.origin.name,
                destinationName: search.destination.name,
                smartcard: search.smartcard
            }
        );
        this.event('search:season', data);
        this.sitecoreSearch(search, true);
    }

    loggedIn ({ user }) {
        cachedUserIDHashed = null;
        cachedUserIDHashPending = computeUserIDHash(user.id);
        if (cachedUserIDHashPending) {
            cachedUserIDHashPending.then((hash) => {
                cachedUserIDHashed = hash;
                cachedUserIDHashPending = null;
                this.event('login', { userID: user.id });
                this.sitecoreIdentity(hash);
            });
            return;
        }
        this.event('login', { userID: user.id });
    }

    pageView ({ location: routeLocation }) {
        const route = urls.resolve(routeLocation.pathname);
        const title = t(`pageTitles.${route.name}`, null, t('pageTitles.default'));
        this.event(`page:${route.name}`, {
            'pageURL': location(),
            'pageTitle': title
        });
        if (this.isSitecoreEnabled()) {
            this.sitecoreView();
        } else {
            window.__sitecore_view_pending = true;
        }
    }

    widgetStationSelector ({ search }) {
        this.event(`widget-search:event:select-${search.type}`, {
            'pageURL': location(),
            'station': search.stationName
        });
    }

    widgetStationSwitch () {
        this.event(`widget-search:event:station-switch`, {
            'pageURL': location(),
        });
    }

    widgetSearchClick ({type}) {
        this.event(`widget-search:event:click-${type.name}`, {
            ...(type.value && { 'value': type.value }),
            'pageURL': location(),
        });
    }

    widgetDaySelect ({ data }) {
        this.event(`widget-search:event:calendar-day-select${data.type ? '-' + data.type : ''}`, {
            'value': data.date.toDate(),
            'pageURL': location(),
        });
    }

    faresExpand ({type}) {
        this.event(`fares-click:${cleanEventName(type.name)}`, {
            'pageURL': location(),
        });
    }

    plusBusSelect ({type}) {
        this.event(`plus-bus:${type.action}:${type.name}`, {
            'pageURL': location(),
        });
    }

    externalLink ({type}) {
        this.event(`click-external-link:${type.name}`, {
            'pageURL': location(),
        });
    }

    modalOpened({ type }) {
        function functionName(fun) {
            let out = fun.toString();
            out = out.substr('function '.length);
            out = out.substr(0, out.indexOf('('));
            return out;
        }
        if (_.isFunction(type)) {
            type = functionName(type);
        }
        this.event(`modal:${type.toLowerCase()}`, { 'pageURL': location() });
    }

    genericModalOpened ({ type }) {
        const cleanModalName = cleanEventName(type);
        if (type === GenericModals.JOURNEY_DETAILS_FORM) {
            hotJarTrigger(type);
        }
        this.event(`modal:${cleanModalName}`);
    }

    orderPaid ({ order, appliedVoucher }) {
        let data = EcommerceHelper.purchase(order, appliedVoucher);
        data.extra = {
            'deliveryOption': order.delivery.displayName,
        };

        this.event('order:paid', data);
        this.sitecoreOrderCheckout(order);
    }

    voucherApplied ({ voucher }) {
        if (!voucher) {
            return;
        }
        this.event('order:voucherApplied', {
            voucher: {
                code: voucher.code,
                amount: toPounds(voucher.amount),
            },
        });
    }

    orderCreated ({ order, trip }) {
        this.event('order:started', EcommerceHelper.orderStarted(order.id));
        this.addToBasket({ orderTrip: order.trips.first(), trip });
        this.addTripWithPassengerAssist();
    }

    deliveryMethod ({ order, eventType = 'main' }) {
        if (eventType === 'main') {
            this.event('order:deliveryMethods', EcommerceHelper.deliveryPage(order.id));
        } else {
            this.event('order:deliveryMethodSelected', EcommerceHelper.deliveryMethod(order.id, order.delivery.internalName));
        }
    }

    paymentMethod ({ id, paymentMethod, eventType = 'main' }) {
        if (eventType === 'main') {
            this.event('order:paymentMethods', EcommerceHelper.paymentPage(id));
        } else {
            cachedPaymentMethod = paymentMethod;
            this.event('order:paymentMethodSelected', EcommerceHelper.paymentMethod(id, paymentMethod));
        }
    }

    addToBasket ({ orderTrip, trip }) {
        let data = EcommerceHelper.addToBasket(orderTrip);
        if (trip.search.targetLayout) {
            data.extra = {
                jpLayout: trip.search.targetLayout.toLowerCase()
            };

            if (trip.search.targetLayout === JourneyPlannerLayout.GRID) {
                data.extra.preselectedBasket = trip.raw ? 'yes' : 'no';
            }
        }
        this.event('order:addToBasket', data);
        this.addTripWithPassengerAssist();
    }

    removeFromBasket ({ orderTrip }) {
        this.event('order:removeFromBasket', EcommerceHelper.removeFromBasket(orderTrip));
        this.removeTripWithPassengerAssist();
    }

    returnTravelcardAdded ({ orderTrip }) {
        if (orderTrip) {
            this.event('order:returnTravelcardUpgrade', EcommerceHelper.addToBasket(orderTrip));
        }
    }

    earlierLater (timeDirection) {
        return ({ tripDirection }) => {
            this.event(`page:event:${tripDirection.toLowerCase()}-${timeDirection.toLowerCase()}-click`);
        };
    }

    jpChangedSearchType ({ searchType }) {
        this.event(`page:event:changed-search-type-to-${searchType}`);
    }

    jpCheckoutWithSuggestedProducts () {
        this.event(`page:event:first-action-add-to-basket`);
    }

    jpAllFaresAccordionClick ({ value }) {
        this.event(`page:event:view-all-fares-${(value ? 'open' : 'close')}`);
    }

    jpTocSpecificFilterClick ({ value }) {
        this.event(`page:event:toc-specific-checkbox-${(value ? 'on' : 'off')}`);
    }

    jpTocSpecificFilterRender () {
        this.event(`page:event:render:toc-specific-filter-rendered`);
    }

    jpChangeFareClassFilter ({ fareClass, value }) {
        this.event(`page:event:${fareClass}-checkbox-${(value ? 'on' : 'off')}`);
    }

    jpChangeFareSelectionFilter ({ selection }) {
        this.event(`page:event:filter-${selection.toLowerCase()}-selected`);
    }

    jpChangeLayout ({layout}) {
        this.event(`page:event:${layout.toLowerCase()}-tab-click`);
    }

    handleTutorialToggle ({ tutorialName, open, dismissed, lastStage, currentStage }) {
        const close = `-close-${currentStage}-of-${lastStage}`;
        this.event(`modal:tutorial-${tutorialName}${(open ? '' : close)}`);
        if (dismissed) {
            this.event(`modal:tutorial-${tutorialName}-dismissed`);
        }
    }

    stationFinderSearch ({ searchInteraction, latitude, longitude }) {
        this.event(`page:event:find-station:${searchInteraction}:${latitude},${longitude}`);
    }

    stationFinderSelected ({ originDest, station }) {
        this.event(`page:event:found-station-used:${originDest}:${station.nlc}-${station.name}`);
    }

    refundComplete ({ isFlexiRefund }) {
        this.event(`page:event:refund-completed${(isFlexiRefund ? ':is-flexi' : '')}`);
    }

    addFavourite ({ context }) {
        this.event(`page:event:favourite-add${(context ? ':' + context : '')}`);
    }

    toggleJobcenterPlusRailcard ({ type }) {
        this.event(type);
    }

    removeFavourite ({ context }) {
        this.event(`page:event:favourite-remove${(context ? ':' + context : '')}`);
    }

    addFavouriteToBasket ({ success }) {
        this.event(`page:event:favourite-added-to-basket:${success ? 'successful' : 'failed'}`);
    }

    addReorderToBasket ({ success }) {
        this.event(`page:event:reorder-added-to-basket:${success}`);
    }

    addDisruptionAlert ({ context }) {
        this.event(`page:event:disruption-alert-add${(context ? ':' + context : '')}`);
    }

    deleteDisruptionAlert ({ context }) {
        this.event(`page:event:disruption-alert-delete${(context ? ':' + context : '')}`);
    }

    submitAutoDRClaim ({ paymentId, editDelayLength }) {
        const paymentMethodString = _.get(_.invert(DelayRepayPaymentMethod), paymentId).toLowerCase();
        const amendDelayLenth = Boolean(editDelayLength);
        this.event(`page:event:auto_dr_claim_submitted:amend-length-${amendDelayLenth}:payment-method-${paymentMethodString}`);
    }

    confirmAmendBooking ({ description }) {
        this.event(`page:event:amend-booking-confirmed:${description}`, null);
    }

    widgetsRender ({ brand, tabs, version }) {
        this.event('widgets:loaded', { brand, tabs, version });
    }

    widgetsViewResults ({ brand, tab, resultsUrl, version }) {
        this.event(`widgets:search-results:${tab}`, { brand, resultsUrl, version });
    }

    trackingEnabled () {
        const cookie = CookieHelper.get(CookieKeys.DO_NOT_TRACK);

        if (cookie) {
            CookieHelper.clear(CookieKeys.DO_NOT_TRACK);
            window.location.reload();
        }
    }

    trackingDisabled () {
        const cookie = CookieHelper.get(CookieKeys.DO_NOT_TRACK);

        if (!cookie) {
            CookieHelper.set(CookieKeys.DO_NOT_TRACK, true);
            window.location.reload();
        }
    }

    isTrackingEnabled () {
        if (CookieHelper.get(CookieKeys.DO_NOT_TRACK)) {
            return false;
        }
        return isGtmConsentGranted();
    }

    showFlexiWarningModal ({ context }) {
        this.event(`modal:openFlexiKeyGoWarning${(context ? ':' + context : '')}`);
    }

    flexiWarningModalAcknowledgementCheckboxChecked ({ context }) {
        this.event(`modal:flexiKeyGoWarningAcknowledgementCheckboxChecked${(context ? ':' + context : '')}`);
    }

    flexiWarningModalAcknowledgementCheckboxUnchecked ({ context }) {
        this.event(`modal:flexiKeyGoWarningAcknowledgementCheckboxUnchecked${(context ? ':' + context : '')}`);
    }

    flexiWarningModalClose ({ context }) {
        this.event(`modal:flexiKeyGoWarningClose${(context ? ':' + context : '')}`);
    }

    flexiWarningModalContinue ({ context }) {
        this.event(`modal:flexiKeyGoWarningContinue${(context ? ':' + context : '')}`);
    }

    flexiWarningModalMoreInfoClicked ({ context }) {
        this.event(`modal:flexiKeyGoWarningMoreInfoClicked${(context ? ':' + context : '')}`);
    }

    userOptedIn ({from}) {
        this.event(`page:event:OCDR-opt-in-${from}`);
    }

    userOptedOut () {
        this.event('page:event:OCDR-optout');
    }

    webchatOpened () {
        this.event('WCWidget:modal:open');
    }

    selectNotificationPreference ({method}) {
        this.event(`page:event:OCDR-notification-${method ? method : 'none'}`);
    }

    selectRepaymentMethod ({method}) {
        this.event(`page:event:OCDR-repayment-${method}`);
    }

    amendNotificationPreference () {
        this.event('page:event:OCDR-notification-amended');
    }

    amendRepaymentMethod () {
        this.event('page:event:OCDR-repayment-amended');
    }

    amendAddress () {
        this.event('page:event:OCDR-address-ammended');
    }

    saveRegularJourneyWithOCDRFavJourney () {
        this.event('page:event:OCDR-journey-with-notification');
    }

    createAccessibilityProfile ({ accessibilityProfile }) {
        this.event('passenger-assist:accessibility-profile-created', { accessibilityProfile });
    }

    updateAccessibilityProfile ({ accessibilityProfile }) {
        this.event('passenger-assist:accessibility-profile-updated', { accessibilityProfile });
    }

    bookPassengerAssist ({ bookedAssist, context }) {
        // Don't fire the event if we're on the payment confirmation page as there is a separate event to track PA bookings from there
        if (context !== 'paymentConfirmation') {
            this.event('passenger-assist:requested', { bookedAssist });
        }
    }

    bookPassengerAssistWithOrder ({ bookedAssist }) {
        this.event('passenger-assist:added-to-the-order', { bookedAssist });
    }

    cancelPassengerAssist ({ sysId }) {
        this.event('passenger-assist:canceled', { sysId });
    }

    editPassengerAssist ({ passengerAssist }) {
        this.event('passenger-assist:amended', { passengerAssist });
    }

    addTripWithPassengerAssist () {
        this.event('passenger-assist:added-to-the-order');
    }

    removeTripWithPassengerAssist () {
        this.event('passenger-assist:removed-from-the-order');
    }

    requestPassengerAssistWithOrder () {
        this.event('passenger-assist:requested-with-order');
    }

    trackChildSmartcardReplacementPageView ({ userId }) {
        this.event('child-smartcard-replacement:page-view', { userId });
    }

    trackChildSmartcardReplacementRequest ({ userId }) {
        this.event('child-smartcard-replacement:request:confirm', { userId });
    }

    signupStepViewed (stepName) {
        this.event('signup_step_viewed', { step_name: stepName });
    }

    signupStepCompleted (stepName) {
        this.event('signup_step_completed', { step_name: stepName });
    }

    signupSmartcardFlowStarted () {
        this.event('signup_smartcard_flow_started');
    }

    signupSmartcardFlowSkipped () {
        this.event('signup_smartcard_flow_skipped');
    }

    signupAttempt (hasSmartcard) {
        this.event('signup_attempt', { has_smartcard: hasSmartcard });
    }

    signupSuccess (hasSmartcard) {
        this.event('signup_success', { has_smartcard: hasSmartcard });
    }

    signupAbandoned (stepName, hasValidationError, hasStartedForm) {
        this.event('signup_abandoned', {
            step_name: stepName,
            has_validation_error: hasValidationError,
            has_started_form: hasStartedForm
        });
    }

    signupFieldInteraction (fieldName) {
        this.event('signup_field_interaction', { field_name: fieldName });
    }

    signupValidationError (fieldName, errorType) {
        this.event('signup_validation_error', {
            field_name: fieldName,
            error_type: errorType
        });
    }

    signupAddressLookupFailed () {
        this.event('signup_address_lookup_failed');
    }

    signupOver16Toggled () {
        this.event('signup_over_16_toggled');
    }

    signupBEFailure (reason, stepName) {
        this.event('signup_BE_failure', {
            reason: reason,
            step_name: stepName
        });
    }
}

const storeInstance = new DataLayerStore();

export default storeInstance;
